import { useDrawer } from '@/core/context/DrawerContext'
import { AppModule } from '@/core/modules/app/app.module'
import { TIntegration } from '@/core/modules/integrations/integrations'
import { IssuesModule } from '@/core/modules/issues'
import { LoaderWithText } from '@/ui/components/loader-with-text'
import { PopConfirm } from '@/ui/components/popConfirm'
import BetaIcon from '@assets/images/beta-icon.svg?react'
import ErrorIconHexa from '@assets/images/error-icon-hexa.svg?react'
import NewTabIcon from '@assets/images/new-tab-icon.svg?react'
import { Switch } from '@components/buttons/switch'
import { useIntegrations } from '@core/hooks/use-integrations'
import { IntegrationForm } from '@ui/drawer/integration/integrationForm'
import { FC } from 'react'
import { IntegrationSteps } from '../integrationSteps'
import {
    ErrorWrapper,
    IconWrapper,
    StyledActionButton,
    TitleWrapper,
    ToggleWrapper,
    TopSection,
} from './integrationDetails.styles'
import {
    generateIntegrationError,
    integrationDetailsByType,
} from '@ui/drawer/integration/utils/utils'
import Typography from '@/ui/components/typography'
import { keyErrorInfo } from '@/core/modules/issues/constants'
import { IntegrationsStore } from '@/core/store/Integrations'

type TIntegrationDetailsProps = {
    integration: TIntegration
}

const IntegrationDetails: FC<TIntegrationDetailsProps> = ({ integration }) => {
    const { setDrawerState } = useDrawer()
    const { scanInProgress } = AppModule.useStore()
    const { formStructure } = useIntegrations()
    const {
        display_name: displayName,
        name,
        description,
        type,
        status,
        loading,
        error,
        integration_id: integrationId,
        id: providerId,
    } = integration
    const { icon, isBeta } = integrationDetailsByType?.[type] ?? {}
    const isIntegrationEnabled = status === 'active'
    const issues = IssuesModule.getIntegrationIssuesById(type, integrationId)
    const isIntegrationRequestsPending =
        IntegrationsStore.getPendingIntegrationProvidersIds()
    const isIntegrationPending =
        isIntegrationRequestsPending.includes(providerId)

    if (
        typeof error === 'string' &&
        error.includes(keyErrorInfo.maxUsersExceeded.key!)
    ) {
        setDrawerState((prevState) => ({
            ...prevState,
            isDrawerOpen: false,
            drawerContentType: null,
        }))
        integration.error = undefined
    }

    const confirmationText =
        issues?.length > 1
            ? `Are you sure you want to disable ${displayName} integration? There are ${issues?.length} issues still open`
            : issues?.length === 1
              ? `Are you sure you want to disable ${displayName} integration? There is 1 issue still open`
              : `Are you sure you want to disable ${displayName} integration?`

    const toggleIntegration = (code: string | undefined) => {
        if (integration.status === 'active') {
            setDrawerState((prevState) => ({
                ...prevState,
                isDrawerOpen: false,
                drawerContentType: null,
            }))
        }
    }

    return (
        <>
            <TopSection>
                <TitleWrapper>
                    {isBeta && <BetaIcon />}
                    <Typography
                        component="p"
                        variant="l-regular"
                        testId={`integration-name-${name}`}
                    >
                        {displayName}
                    </Typography>
                </TitleWrapper>
                {icon && <IconWrapper>{icon}</IconWrapper>}
                <Typography component="p" variant="xs-regular">
                    {description}
                </Typography>

                {isIntegrationPending && (
                    <LoaderWithText
                        text={'Waiting for authorization...'}
                        direction="ltr"
                    />
                )}
                {!isIntegrationEnabled && !loading && (
                    <>
                        <IntegrationSteps integrationType={type} />
                        {formStructure[type]?.length > 0 ? (
                            <IntegrationForm
                                integration={integration}
                                formStructure={formStructure[type]}
                            />
                        ) : (
                            <StyledActionButton
                                text={`Enable Integration`}
                                icon={<NewTabIcon />}
                                disabled={isIntegrationPending}
                                onClick={toggleIntegration}
                            />
                        )}
                    </>
                )}
                {isIntegrationEnabled && !loading && (
                    <PopConfirm
                        title="Disable integration"
                        description={confirmationText}
                        onConfirm={toggleIntegration}
                    >
                        <ToggleWrapper>
                            <Switch
                                disabled={scanInProgress}
                                testId={`${name}-active`}
                                checked={isIntegrationEnabled}
                                loading={loading}
                            />
                        </ToggleWrapper>
                    </PopConfirm>
                )}
                {loading && (
                    <LoaderWithText
                        text={'Waiting for authorization...'}
                        direction="ltr"
                    />
                )}
                {isIntegrationEnabled && scanInProgress && (
                    <LoaderWithText
                        text={'Scan in progress...'}
                        direction="ltr"
                    />
                )}
            </TopSection>
        </>
    )
}

export default IntegrationDetails
